import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useUI } from '../contexts/UIContext';
import { useTranslation } from '../providers/TranslationProvider';
import { SCREENS } from '../utils/constants';
import { CheckCircle } from 'lucide-react';
import LetterGlitch from '../components/LetterGlitch';
import Logo from '../icons/logo.svg';

const AccountCreatedSuccessScreen = () => {
  const { navigateToScreen } = useUI();
  const { t } = useTranslation();
  const [countdown, setCountdown] = useState(5);

  // Theme state
  const [isDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved ? saved === 'dark' : window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(prev => prev - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      navigateToScreen(SCREENS.LOGIN);
    }
  }, [countdown, navigateToScreen]);

  // Animation Variants
  const containerVariants = {
    initial: { opacity: 0, x: 20 },
    animate: { opacity: 1, x: 0, transition: { duration: 0.5, ease: "easeOut" } },
    exit: { opacity: 0, x: -20, transition: { duration: 0.3 } }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut", delay: 0.2 } }
  };

  const bgClass = isDarkMode ? 'bg-slate-900' : 'bg-slate-50';
  const textClass = isDarkMode ? 'text-white' : 'text-slate-900';
  const cardBg = isDarkMode ? 'bg-slate-900/60 border-slate-800' : 'bg-white/80 border-white';

  return (
    <motion.div
      className={`relative w-full h-screen overflow-hidden flex flex-col items-center justify-center ${bgClass} ${textClass}`}
      variants={containerVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      {/* Dynamic Background */}
      <div className={`absolute inset-0 transition-colors duration-700 ${isDarkMode ? 'bg-slate-950' : 'bg-gradient-to-br from-blue-50 to-white'}`}>
        {isDarkMode && (
          <div className="absolute inset-0 opacity-30 pointer-events-none">
            <LetterGlitch
              glitchColors={['#1e40af', '#3b82f6', '#60a5fa']}
              glitchSpeed={50}
              centerVignette={true}
              outerVignette={false}
              smooth={true}
            />
          </div>
        )}
      </div>

      {/* Main Content */}
      <motion.div
        className={`relative z-10 w-full max-w-md p-8 rounded-3xl backdrop-blur-xl border shadow-2xl ${cardBg} text-center`}
        variants={cardVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="w-24 h-24 mx-auto bg-green-500/10 rounded-full flex items-center justify-center mb-6">
          <CheckCircle size={48} className="text-green-500" />
        </div>
        <h2 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-indigo-600 mb-2">
          {t('success.title', 'Account Created!')}
        </h2>
        <p className={`text-lg mb-6 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
          {t('success.redirecting', 'Redirecting you to login in')} <span className="font-bold text-blue-500">{countdown}</span>
        </p>

        <p className={`text-sm mb-8 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
          {t('success.emailSent', 'We sent a verification email to your inbox.')}
        </p>

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => navigateToScreen(SCREENS.LOGIN)}
          className={`w-full py-4 rounded-xl font-bold text-lg shadow-lg relative overflow-hidden ${isDarkMode
              ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-blue-900/30'
              : 'bg-blue-600 text-white shadow-blue-200'
            }`}
        >
          {t('success.loginNow', 'Login Now')}
        </motion.button>
      </motion.div>
    </motion.div>
  );
};

export default AccountCreatedSuccessScreen;
